import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MessageCircle, Plus, Send, Clock, User, CheckCircle } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

interface Question {
  id: string;
  title: string;
  content: string;
  module_name: string;
  created_at: string;
  user_id: string;
  user_profiles_2025_11_15_09_09?: {
    full_name: string;
    username: string;
  };
  answers_2025_11_15_09_09?: Answer[];
}

interface Answer {
  id: string;
  content: string;
  is_instructor_answer: boolean;
  created_at: string;
  user_id: string;
  user_profiles_2025_11_15_09_09?: {
    full_name: string;
    username: string;
  };
}

const modules = [
  "Introduction to Machine Learning",
  "Data Preprocessing & Analysis", 
  "Supervised Learning - Regression",
  "Supervised Learning - Classification",
  "Unsupervised Learning",
  "Neural Networks & Deep Learning",
  "Model Evaluation & Deployment",
  "Capstone Project",
  "General Questions"
];

export const QASection = () => {
  const { user } = useAuth();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewQuestion, setShowNewQuestion] = useState(false);
  const [newQuestion, setNewQuestion] = useState({ title: '', content: '', module_name: '' });
  const [newAnswer, setNewAnswer] = useState<{ [key: string]: string }>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      const { data, error } = await supabase
        .from('questions_2025_11_15_09_09')
        .select(`
          *,
          user_profiles_2025_11_15_09_09(full_name, username),
          answers_2025_11_15_09_09(
            *,
            user_profiles_2025_11_15_09_09(full_name, username)
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setQuestions(data || []);
    } catch (error: any) {
      toast({
        title: "Error loading questions",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      toast({
        title: "Please log in",
        description: "You need to be logged in to ask questions",
        variant: "destructive",
      });
      return;
    }

    if (!newQuestion.title.trim() || !newQuestion.content.trim() || !newQuestion.module_name) {
      toast({
        title: "Missing information",
        description: "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('questions_2025_11_15_09_09')
        .insert([{
          title: newQuestion.title.trim(),
          content: newQuestion.content.trim(),
          module_name: newQuestion.module_name,
          user_id: user.id
        }]);

      if (error) throw error;

      toast({
        title: "Question posted!",
        description: "Your question has been posted successfully",
      });

      setNewQuestion({ title: '', content: '', module_name: '' });
      setShowNewQuestion(false);
      fetchQuestions();
    } catch (error: any) {
      toast({
        title: "Error posting question",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitAnswer = async (questionId: string) => {
    if (!user) {
      toast({
        title: "Please log in",
        description: "You need to be logged in to answer questions",
        variant: "destructive",
      });
      return;
    }

    const answerContent = newAnswer[questionId]?.trim();
    if (!answerContent) {
      toast({
        title: "Empty answer",
        description: "Please write an answer before submitting",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('answers_2025_11_15_09_09')
        .insert([{
          question_id: questionId,
          content: answerContent,
          user_id: user.id,
          is_instructor_answer: false
        }]);

      if (error) throw error;

      toast({
        title: "Answer posted!",
        description: "Your answer has been posted successfully",
      });

      setNewAnswer({ ...newAnswer, [questionId]: '' });
      fetchQuestions();
    } catch (error: any) {
      toast({
        title: "Error posting answer",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <section id="qa" className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-300 rounded w-64 mx-auto mb-4"></div>
              <div className="h-4 bg-gray-300 rounded w-96 mx-auto"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="qa" className="py-20 px-4 bg-gray-50">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            Q&A <span className="text-blue-600">Community</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Ask questions, share knowledge, and learn from fellow students and instructors
          </p>
        </div>

        {/* Ask Question Section */}
        <div className="mb-12">
          {!showNewQuestion ? (
            <Card className="border-dashed border-2 border-blue-200 bg-blue-50/30">
              <CardContent className="p-8 text-center">
                <MessageCircle className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Have a Question?</h3>
                <p className="text-muted-foreground mb-4">
                  Ask the community and get help from instructors and fellow students
                </p>
                <Button 
                  onClick={() => setShowNewQuestion(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Ask a Question
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Ask a New Question</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmitQuestion} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Module</label>
                    <Select 
                      value={newQuestion.module_name} 
                      onValueChange={(value) => setNewQuestion({...newQuestion, module_name: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a module" />
                      </SelectTrigger>
                      <SelectContent>
                        {modules.map((module) => (
                          <SelectItem key={module} value={module}>
                            {module}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Question Title</label>
                    <Input
                      value={newQuestion.title}
                      onChange={(e) => setNewQuestion({...newQuestion, title: e.target.value})}
                      placeholder="What's your question about?"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Question Details</label>
                    <Textarea
                      value={newQuestion.content}
                      onChange={(e) => setNewQuestion({...newQuestion, content: e.target.value})}
                      placeholder="Provide more details about your question..."
                      rows={4}
                      required
                    />
                  </div>
                  <div className="flex space-x-2">
                    <Button type="submit" disabled={submitting}>
                      {submitting ? 'Posting...' : 'Post Question'}
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setShowNewQuestion(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Questions List */}
        <div className="space-y-6">
          {questions.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <MessageCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No questions yet</h3>
                <p className="text-muted-foreground">
                  Be the first to ask a question and start the discussion!
                </p>
              </CardContent>
            </Card>
          ) : (
            questions.map((question) => (
              <Card key={question.id} className="overflow-hidden">
                <CardHeader className="bg-gray-50/50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <Badge variant="outline">{question.module_name}</Badge>
                        <div className="flex items-center space-x-1 text-sm text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          <span>{formatDate(question.created_at)}</span>
                        </div>
                      </div>
                      <CardTitle className="text-lg">{question.title}</CardTitle>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="mb-6">
                    <div className="flex items-center space-x-2 mb-3">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback>
                          {question.user_profiles_2025_11_15_09_09?.full_name?.[0] || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-sm">
                          {question.user_profiles_2025_11_15_09_09?.full_name || 'Anonymous'}
                        </p>
                        <p className="text-xs text-muted-foreground">Student</p>
                      </div>
                    </div>
                    <p className="text-muted-foreground">{question.content}</p>
                  </div>

                  {/* Answers */}
                  {question.answers_2025_11_15_09_09 && question.answers_2025_11_15_09_09.length > 0 && (
                    <div className="space-y-4 mb-6">
                      <h4 className="font-semibold text-sm flex items-center">
                        <MessageCircle className="h-4 w-4 mr-2" />
                        Answers ({question.answers_2025_11_15_09_09.length})
                      </h4>
                      {question.answers_2025_11_15_09_09.map((answer) => (
                        <div key={answer.id} className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center space-x-2 mb-2">
                            <Avatar className="h-6 w-6">
                              <AvatarFallback className="text-xs">
                                {answer.user_profiles_2025_11_15_09_09?.full_name?.[0] || 'U'}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex items-center space-x-2">
                              <p className="font-medium text-sm">
                                {answer.user_profiles_2025_11_15_09_09?.full_name || 'Anonymous'}
                              </p>
                              {answer.is_instructor_answer && (
                                <Badge variant="default" className="bg-green-600 text-xs">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Instructor
                                </Badge>
                              )}
                              <span className="text-xs text-muted-foreground">
                                {formatDate(answer.created_at)}
                              </span>
                            </div>
                          </div>
                          <p className="text-sm">{answer.content}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Answer Form */}
                  {user && (
                    <div className="border-t pt-4">
                      <div className="flex space-x-2">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback>
                            {user.email?.[0]?.toUpperCase() || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 space-y-2">
                          <Textarea
                            value={newAnswer[question.id] || ''}
                            onChange={(e) => setNewAnswer({...newAnswer, [question.id]: e.target.value})}
                            placeholder="Write your answer..."
                            rows={3}
                          />
                          <Button 
                            size="sm" 
                            onClick={() => handleSubmitAnswer(question.id)}
                            disabled={!newAnswer[question.id]?.trim()}
                          >
                            <Send className="h-4 w-4 mr-2" />
                            Post Answer
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {!user && (
                    <div className="border-t pt-4 text-center">
                      <p className="text-muted-foreground text-sm">
                        Please log in to post an answer
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </section>
  );
};